import { OptimizelyProduct } from '../types/DataSyncItem';
import { INCOMPLETE } from '../types/FieldMapping';
import {
  FIELD_PATH_SEPARATOR,
  FieldDefinition,
} from '../types/FieldDefinition';
import { FieldMappingEntry } from '../types/DataSyncViewModel';

const hasArrayFields = (
  field: FieldDefinition
): field is FieldDefinition & { array: { fields: FieldDefinition[] } } => {
  return (
    field.array !== undefined &&
    typeof field.array === 'object' &&
    'fields' in field.array
  );
};

export const findFieldByPath = (
  fields: FieldDefinition[],
  path: string
): FieldDefinition | undefined => {
  const pathParts = path.split('/');

  if (pathParts.length === 1) {
    return fields.find((field) => field.name === path);
  }

  const [firstPart, ...remainingParts] = pathParts;
  const parentField = fields.find((field) => field.name === firstPart);

  if (!parentField) {
    return undefined;
  }

  // Handle nested object fields
  if (parentField.object_schema?.fields) {
    return findFieldByPath(
      parentField.object_schema.fields,
      remainingParts.join(FIELD_PATH_SEPARATOR)
    );
  }

  // Handle arrays with object schemas
  if (hasArrayFields(parentField)) {
    return findFieldByPath(
      parentField.array.fields,
      remainingParts.join(FIELD_PATH_SEPARATOR)
    );
  }

  return undefined;
};

export function resetSourceFieldMappings(
  field_mappings: FieldMappingEntry[]
): FieldMappingEntry[] {
  return field_mappings.map(({ destinationField, disabled }) => ({
    destinationField,
    sourceField: INCOMPLETE,
    disabled,
  }));
}

export function resetDestinationFieldMappings(
  field_mappings: FieldMappingEntry[]
): FieldMappingEntry[] {
  return field_mappings.map(({ sourceField, disabled }) => ({
    destinationField: INCOMPLETE,
    sourceField,
    disabled,
  }));
}

/**
 * Filters out system fields that should not be shown in the field mappings UI.
 * Currently filters out: _metadata
 */
export const filterSystemFields = (fields: FieldDefinition[]): FieldDefinition[] => {
  return fields.filter((field) => field.name !== '_metadata');
};

export function getObjectLabel(product: string): string {
  return product === OptimizelyProduct.CGR ? 'Content Type' : 'Object';
}

export function getNotificationLabelForObject(product: string): string {
  return product === OptimizelyProduct.CGR ? 'a content type' : 'an object';
}

export function getSchemaChangesMessage(
  mappings: FieldMappingEntry[],
  sourceFields: FieldDefinition[],
  destinationFields: FieldDefinition[]
): string | null {
  if (mappings.length === 0) {
    return null;
  }

  const orphanedDest = mappings
    .filter((m) => m.destinationField && !findFieldByPath(destinationFields, m.destinationField))
    .map((m) => m.destinationField);

  const orphanedSource = mappings
    .filter((m) => m.sourceField && !findFieldByPath(sourceFields, m.sourceField))
    .map((m) => m.sourceField);

  if (orphanedDest.length === 0 && orphanedSource.length === 0) {
    return null;
  }

  const parts: string[] = [];
  if (orphanedDest.length > 0) {
    parts.push(`destination field(s) ${orphanedDest.join(', ')}`);
  }
  if (orphanedSource.length > 0) {
    parts.push(`source field(s) ${orphanedSource.join(', ')}`);
  }

  return `Schema changes detected: ${parts.join(' and ')} no longer exist in the current schema.`;
}

export function filterOrphanedMappings(
  mappings: FieldMappingEntry[],
  destinationFields: FieldDefinition[]
): FieldMappingEntry[] {
  return mappings.filter((m) =>
    findFieldByPath(destinationFields, m.destinationField)
  );
}
